import { getAccessToken, getWsId } from '../lib/utility';
import { authService } from './authService';
export type RequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

// Use the environment variable
const VITE_API_URL = import.meta.env.VITE_API_URL;
const TOKEN_COOKIE_NAME = 'fs3_access_token';
const WS_ID_COOKIE_NAME = 'fs3_selected_ws_id';

export interface RequestOptions<T> {
  method?: RequestMethod;
  data?: T;
  signal?: AbortSignal;
}

export async function request<T, R = any>(
  url: string,
  options: RequestOptions<T> = {}
): Promise<R> {
  const { method = 'GET', data } = options;

  const fullUrl = `${VITE_API_URL}${url}`;

  // Retrieve the access token from cookies
  const accessToken = getAccessToken(TOKEN_COOKIE_NAME);
  const workspaceId = getWsId(WS_ID_COOKIE_NAME);
  const documentURL = document.URL;

  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-FRAME-OPTIONS': 'SAMEORIGIN',
      ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      ...(workspaceId && { 'fs-workspace-id': workspaceId }),
      ...(documentURL && { 'X-Current-Url': documentURL }),
    },
  };

  if (data) {
    config.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(fullUrl, config);

    // Check if response is not OK (i.e., status code is not in the range of 200-299)
    if (!response.ok) {
      throw response;
    }

    // Type assertion: explicitly cast response.json() to type R
    const responseData: R = await response.json();
    return responseData;
  } catch (error: any) {
    // if we get 401 error, logout the user
    if (error.status === 401) {
      authService.clearAuthSession();
    }

    const errorData: R = await error.json();
    return Promise.reject(errorData);
  }
}
const getRequest = <R>(url: string) => request<null, R>(url);
const postRequest = <T, R>(url: string, data: T) => request<T, R>(url, { method: 'POST', data });
const putRequest = <T, R>(url: string, data: T) => request<T, R>(url, { method: 'PUT', data });
const deleteRequest = <R>(url: string) => request<null, R>(url, { method: 'DELETE' });

const postFormDataRequest = async <R>(url: string, formData: FormData) => {
  const fullUrl = `${VITE_API_URL}${url}`;

  // Retrieve the access token from cookies
  const accessToken = getAccessToken(TOKEN_COOKIE_NAME);
  const workspaceId = getWsId(WS_ID_COOKIE_NAME);
  const documentURL = document.URL;

  const config = {
    method: 'POST',
    headers: {
      'X-FRAME-OPTIONS': 'SAMEORIGIN',
      ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      ...(workspaceId && { 'fs-workspace-id': workspaceId }),
      ...(documentURL && { 'X-Current-Url': documentURL }),
    },
    body: formData
  };

  try {
    const response = await fetch(fullUrl, config);

    // Check if response is not OK (i.e., status code is not in the range of 200-299)
    if (!response.ok) {
      throw response;
    }

    // Type assertion: explicitly cast response.json() to type R
    const responseData: R = await response.json();
    return responseData;
  } catch (error: any) {
    // if we get 401 error, logout the user
    if (error.status === 401) {
      authService.clearAuthSession();
    }

    const errorData: R = await error.json();
    return Promise.reject(errorData);
  }
};

// Add streamRequest for streaming data
async function streamRequest<T>(
  url: string,
  options: RequestOptions<T> = {},
  onChunk: (chunk: any) => void // Callback for handling each streamed chunk
): Promise<void> {
  const { method = 'GET', data, signal } = options;

  const fullUrl = `${VITE_API_URL}${url}`;

  // Retrieve the access token from cookies
  const accessToken = getAccessToken(TOKEN_COOKIE_NAME);
  const workspaceId = getWsId(WS_ID_COOKIE_NAME);
  const documentURL = document.URL;

  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-FRAME-OPTIONS': 'SAMEORIGIN',
      ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      ...(workspaceId && { 'fs-workspace-id': workspaceId }),
      ...(documentURL && { 'X-Current-Url': documentURL }),
    },
    signal, // Pass the AbortSignal for cancellation if provided
  };

  if (data) {
    config.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(fullUrl, config);

    // Ensure response is OK
    if (!response.ok) {
      const errorData = await response.json();
      throw errorData;
    }

    // Process the streamed response
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    if (!reader) {
      throw new Error('Failed to read the response body as a stream.');
    }

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Decode the chunk and append to the buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete chunks separated by \n\n (SSE format)
      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n\n')) !== -1) {
        const chunk = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 2); // Remove processed chunk

        if (chunk.startsWith('data:')) {
          const jsonData = chunk.slice(5).trim(); // Remove "data:" prefix
          try {
            const parsedData = JSON.parse(jsonData);
            onChunk(parsedData); // Pass the parsed data to the callback
          } catch (error) {
            console.error('Error parsing JSON chunk:', jsonData, error);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error in streaming request:', error);
    throw error;
  }
}

export const httpService = {
  getRequest,
  postRequest,
  postFormDataRequest,
  putRequest,
  deleteRequest,
  streamRequest,
};